// Atlas Dashboard — Search Page
// Global search across all Atlas data.

import { useState } from "react";
import { Search as SearchIcon, Mail, User, MessageCircle, Megaphone } from "lucide-react";
import { useSearch } from "../hooks/useApi";

export function SearchPage() {
  const [query, setQuery] = useState("");
  const { data, loading, error } = useSearch(query || null);

  const typeIcons: Record<string, typeof Mail> = {
    email: Mail,
    contact: User,
    conversation: MessageCircle,
    campaign: Megaphone,
  };

  const typeLabels: Record<string, string> = {
    email: "Email",
    contact: "Contact",
    conversation: "Conversation",
    campaign: "Campaign",
  };

  return (
    <div>
      {/* Search bar */}
      <div style={{ position: "relative", marginBottom: "24px" }}>
        <SearchIcon size={18} style={{ position: "absolute", left: "14px", top: "12px", color: "var(--color-text-tertiary)" }} />
        <input
          type="text"
          placeholder="Search emails, contacts, conversations, campaigns..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{
            width: "100%",
            padding: "12px 16px 12px 42px",
            border: "1px solid var(--color-border)",
            borderRadius: "8px",
            fontSize: "15px",
            backgroundColor: "var(--color-card)",
            color: "var(--color-text-primary)",
            outline: "none",
            boxSizing: "border-box" as const,
          }}
        />
      </div>

      {/* Results */}
      {loading && <div style={{ padding: "24px", textAlign: "center", color: "var(--color-text-tertiary)" }}>Searching...</div>}

      {error && <div style={{ padding: "24px", textAlign: "center", color: "#DC2626" }}>{error}</div>}

      {data && data.results.length === 0 && (
        <div style={{ padding: "48px", textAlign: "center" }}>
          <SearchIcon size={48} style={{ color: "var(--color-text-tertiary)", margin: "0 auto 16px" }} />
          <p style={{ color: "var(--color-text-secondary)", fontSize: "14px" }}>
            No results for "{query}". Try a different search term.
          </p>
        </div>
      )}

      {data && data.results.length > 0 && (
        <div>
          {data.note && (
            <p style={{ fontSize: "12px", color: "var(--color-text-tertiary)", marginBottom: "16px" }}>{data.note}</p>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {data.results.map((result, i) => {
              const Icon = typeIcons[result.type] || Mail;
              return (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    padding: "14px 16px",
                    backgroundColor: "var(--color-card)",
                    border: "1px solid var(--color-border)",
                    borderRadius: "8px",
                    cursor: "pointer",
                    transition: "background 0.15s",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#F9FAFB")}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "var(--color-card)")}
                >
                  <Icon size={20} color="var(--color-text-tertiary)" />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: "14px", fontWeight: 600, color: "var(--color-text-primary)" }}>{result.title}</div>
                    <div style={{ fontSize: "13px", color: "var(--color-text-secondary)" }}>{result.subtitle}</div>
                    <div style={{ fontSize: "12px", color: "var(--color-text-tertiary)", marginTop: "2px" }}>{result.detail}</div>
                  </div>
                  <span style={{ padding: "2px 8px", borderRadius: "4px", backgroundColor: "#F3F4F6", color: "var(--color-text-secondary)", fontSize: "12px", fontWeight: 600 }}>
                    {typeLabels[result.type] || result.type}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!query && !loading && (
        <div style={{ padding: "48px", textAlign: "center" }}>
          <SearchIcon size={48} style={{ color: "var(--color-text-tertiary)", margin: "0 auto 16px" }} />
          <p style={{ color: "var(--color-text-secondary)", fontSize: "14px" }}>
            Start typing to search across your tracked emails, contacts, conversations, and campaigns.
          </p>
        </div>
      )}
    </div>
  );
}
